// ** React Imports
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { connect, useDispatch, useSelector } from "react-redux";
import {
	registerVerify,
	setFakeUserData,
	setPhoneNumber,
	setSentSmsCodeTime,
} from "store/modules/auth";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { OTPInput } from "input-otp";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import { OtpSlot } from "components/layout/Otp/OtpSlot";
import Tooltip from "components/layout/Tooltip";
import StepLabel from "../Register/components/StepLabel";
import { ButtonLanding } from "components/layout/Button/ButtonLanding";
import { RecoverButtonStyled } from "containers/Landing/styles/RecoverButton.styled";

// ** Hooks
import { useVerifySmsCode } from "./hooks/useVerifySmsCode";
import { useResendSmsCode } from "./hooks/useResendSmsCode";
import { useBlockResendSms } from "./hooks/useBlockResendSms";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import { toast } from "helpers/toastWrapper";

// ** Routes
import routes from "containers";

// #####################################################

const RegisterVerifySubmitCode = () => {
	const { t } = useTranslation(["common", "landing"]);

	const { login, id, hash } = useSelector(
		(state) => state.globalCommon.registrationVerifyPhoneModal
	);

	const history = useHistory();
	const dispatch = useDispatch();

	const [code, setCode] = useState("");
	const [allowChangePhoneNumber, setAllowChangePhoneNumber] = useState(false);

	const phoneNumber = useSelector((state) => state.auth.phoneNumber);
	const countryCode = useSelector((state) => state.auth.countryCode);
	const isoCountryCode = useSelector((state) => state.auth.isoCountryCode);

	const { verifySmsCode, isVerifying } = useVerifySmsCode();
	const { isSending, resendSmsCode } = useResendSmsCode();
	const { isResendDisabledByTime, remainingTimeString } = useBlockResendSms();

	useEffect(() => {
		api.post(`/authorization/registration/check`, {
			id: +id,
			hash,
			login,
		})
			.then((res) => {
				setAllowChangePhoneNumber(res?.data?.sms?.allow_change);
				dispatch(setFakeUserData(res?.data));
				dispatch(
					setSentSmsCodeTime({
						sentTime: res.data?.sms?.send,
						blockTime: res.data?.sms?.block,
					})
				);
				dispatch(
					setPhoneNumber({
						phoneNumber: res.data?.sms?.number,
						countryCode: res.data?.sms?.country,
						isoCountryCode: res.data?.sms?.countryISO,
					})
				);
			})
			.catch((err) => {
				// Przekierowanie na "Moje Dane" w przypadku potwierdzenia rejestracji
				if (err?.response?.status === 406) {
					toast.error(err?.response?.data?.error);
					history.push(`${routes.userpanel.path}/data`);
				}
			});
	}, []);

	// #####################################################

	return (
		<Block flex column fullWidth>
			<StepLabel label={t("landing:register.otp.step2.title")} />
			<Block flex column fullWidth withRowGap>
				<Block column flex justifyCenter textCenter>
					<Text textCenter>
						{t("landing:register.otp.step2.infoContent")}
						<Text bold>
							{" +"}
							{countryCode} {phoneNumber}
						</Text>
					</Text>
				</Block>
				<Block mb={4}>
					<OTPInput
						maxLength={6}
						onChange={(newCode) => {
							setCode(newCode);
						}}
						pushPasswordManagerStrategy={false}
						render={({ slots }) => (
							<Block flex justifyCenter style={{ gap: "3rem" }}>
								<Block flex style={{ gap: "1rem" }}>
									{slots.slice(0, 3).map((slot, idx) => (
										<OtpSlot key={idx} {...slot} />
									))}
								</Block>
								<Block flex style={{ gap: "1rem" }}>
									{slots.slice(3).map((slot, idx) => (
										<OtpSlot key={idx} {...slot} />
									))}
								</Block>
							</Block>
						)}
					/>
				</Block>

				<ButtonLanding
					block
					label={t("landing:register.otp.step2.buttonText")}
					onClick={() => {
						verifySmsCode({ code });
					}}
					disabled={isVerifying}
				>
					{t("landing:register.otp.step2.buttonText")}
				</ButtonLanding>
				<Block flex column justifyCenter alignCenter withRowGap>
					<Block>
						<Text size="0.9rem">
							{t("landing:register.otp.step2.resendText")}
						</Text>
					</Block>

					<Tooltip
						text={
							isResendDisabledByTime &&
							`${t(
								"landing:register.otp.step2.resendTooltipText"
							)} ${remainingTimeString}`
						}
					>
						<RecoverButtonStyled
							onClick={() => {
								if (!(isResendDisabledByTime || isSending)) {
									resendSmsCode({
										phoneNumber: `${phoneNumber}`,
										countryCode: `${countryCode}`,
										isoCountryCode: `${isoCountryCode}`,
									});
								}
							}}
							disabled={isResendDisabledByTime || isSending}
							label={t("landing:register.otp.step2.resendLink")}
						>
							{t("landing:register.otp.step2.resendLink")}
						</RecoverButtonStyled>
					</Tooltip>
					{allowChangePhoneNumber && (
						<Tooltip
							text={
								isResendDisabledByTime &&
								`${t(
									"landing:register.otp.step2.resendTooltipText"
								)} ${remainingTimeString}`
							}
						>
							<RecoverButtonStyled
								onClick={() => {
									history.push(
										`/registration-phone/${login}/${id}/${hash}`
									);
								}}
								disabled={isResendDisabledByTime || isSending}
								label={t(
									"landing:register.otp.step2.changePhoneNumberLink"
								)}
							>
								{t(
									"landing:register.otp.step2.changePhoneNumberLink"
								)}
							</RecoverButtonStyled>
						</Tooltip>
					)}
				</Block>
			</Block>
		</Block>
	);
};

// #####################################################

export default connect(null, {
	registerVerify,
})(RegisterVerifySubmitCode);

// #####################################################
